import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CoachSectionData } from '@/types/unifiedGameForm';
import { GameWithDetails } from '@/types/gameSystem';
import { TeamRosterManagement } from './TeamRosterManagement';
import { TeamReadinessConfirmation } from './TeamReadinessConfirmation';
import { PlayerReadinessManager } from './PlayerReadinessManager';
import { JoinRequestApproval } from './JoinRequestApproval';
import { StartingLineupManager } from './StartingLineupManager';
import { BulkPlayerReadinessButton } from './BulkPlayerReadinessButton';
import { GameDayReadiness } from './GameDayReadiness';
import { Clipboard, Loader2, Save, AlertTriangle, Users, Info } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthenticatedUser } from '@/hooks/useAuthenticatedUser';

interface CoachSectionProps {
  gameData: GameWithDetails;
  canEdit: boolean;
  canApprove: boolean;
  onSave: (data: CoachSectionData) => void;
  saving: boolean;
  userTeamId?: number;
  userTeamSide?: 'home' | 'away';
}

export const CoachSection: React.FC<CoachSectionProps> = ({
  gameData,
  canEdit,
  canApprove,
  onSave,
  saving,
  userTeamId,
  userTeamSide
}) => {
  const { user } = useAuthenticatedUser();
  const [formData, setFormData] = useState<CoachSectionData>({
    players: {
      selectedPlayers: [],
      playerPositions: {},
      playerReadiness: {}
    },
    lineup: {
      startingLineup: [],
      substitutes: [],
      formations: ''
    }
  });

  // Track starting lineup separately for proper state sync
  const [currentStartingLineup, setCurrentStartingLineup] = useState<string[]>([]);
  const [gameTableType, setGameTableType] = useState<'games' | 'venue_games' | null>(null);
  const [rosterRefreshKey, setRosterRefreshKey] = useState(0);

  // Detect which table the game is in and load existing form data
  useEffect(() => {
    const detectGameTableAndLoadData = async () => {
      if (!gameData?.id) return;

      try {
        console.log('🔄 Loading existing coach submissions and roster for game:', gameData.id);
        
        // First detect which table this game is in
        const { data: venueGame } = await supabase
          .from('venue_games')
          .select('id')
          .eq('id', gameData.id)
          .maybeSingle();

        const { data: regularGame } = await supabase
          .from('games')
          .select('id')
          .eq('id', gameData.id)
          .maybeSingle();

        let detectedTableType: 'games' | 'venue_games' | null = null;
        if (venueGame) {
          detectedTableType = 'venue_games';
        } else if (regularGame) {
          detectedTableType = 'games';
        }

        console.log('Game table detected:', detectedTableType);
        setGameTableType(detectedTableType);

        // Load existing roster players from game_rosters_live
        const { data: rosterPlayers, error: rosterError } = await supabase
          .from('game_rosters_live')
          .select('player_id, player_type')
          .eq('game_id', gameData.id)
          .eq('team_side', userTeamSide || 'home');

        if (rosterError) {
          console.error('❌ Error loading roster players:', rosterError);
        }

        const rosterPlayerIds = rosterPlayers?.map(p => p.player_id) || [];
        console.log('🎯 Loaded roster player IDs:', rosterPlayerIds);

        // Load any existing coach form submission
        const { data: submissions, error: submissionError } = await supabase
          .from('game_form_submissions')
          .select('*')
          .eq('game_id', gameData.id)
          .eq('section_type', 'coach')
          .eq('user_id', user?.id || '')
          .order('created_at', { ascending: false })
          .limit(1);

        if (submissionError) {
          console.error('❌ Error loading coach submission:', submissionError);
        }

        if (submissions && submissions.length > 0) {
          const submission = submissions[0];
          const submissionData = submission.submission_data as any;
          console.log('📥 Found existing coach submission:', submissionData);
          
          if (submissionData?.lineup?.startingLineup) {
            const existingLineup = submissionData.lineup.startingLineup;
            console.log('Loading existing lineup from form submission:', existingLineup);
            setCurrentStartingLineup(existingLineup);
          }

          // Merge roster players with submission data
          setFormData(prev => ({
            ...prev,
            players: {
              ...prev.players,
              selectedPlayers: rosterPlayerIds.length > 0 ? rosterPlayerIds : (submissionData?.players?.selectedPlayers || [])
            },
            lineup: {
              ...prev.lineup,
              startingLineup: submissionData?.lineup?.startingLineup || []
            }
          }));
        } else if (rosterPlayerIds.length > 0) {
          // No submission but we have roster players - initialize with them
          console.log('📥 No submission found, initializing with roster players');
          setFormData(prev => ({
            ...prev,
            players: {
              ...prev.players,
              selectedPlayers: rosterPlayerIds
            }
          }));
        }
      } catch (error) {
        console.error('❌ Error detecting game table and loading form data:', error);
      }
    };

    if (user && gameData?.id) {
      detectGameTableAndLoadData();
    }
  }, [user, gameData?.id, userTeamSide]);

  const [teamAssociation, setTeamAssociation] = useState<{
    isCoach: boolean;
    isTeamOwner: boolean;
    homeTeamName: string | null;
    awayTeamName: string | null;
    debugInfo: string;
  }>({
    isCoach: false,
    isTeamOwner: false,
    homeTeamName: null,
    awayTeamName: null,
    debugInfo: ''
  });

  const [loading, setLoading] = useState(true);

  // Debug and check team association
  useEffect(() => {
    const checkTeamAssociation = async () => {
      if (!user || !gameData) {
        setTeamAssociation(prev => ({
          ...prev,
          debugInfo: `Missing user (${!!user}) or gameData (${!!gameData})`
        }));
        setLoading(false);
        return;
      }

      console.log('Coach Section Debug:', {
        userId: user.id,
        userTeamId,
        userTeamSide,
        homeTeamId: gameData.home_team_id,
        awayTeamId: gameData.away_team_id,
        gameId: gameData.id
      });

      try {
        let isCoach = false;
        let isTeamOwner = false;
        let homeTeamName = null;
        let awayTeamName = null;
        let debugInfo = '';

        // Get team names
        if (gameData.home_team_id) {
          const { data: homeTeam } = await supabase
            .from('teams')
            .select('team_name')
            .eq('team_id', gameData.home_team_id)
            .maybeSingle();
          homeTeamName = homeTeam?.team_name || 'Home Team';
        }

        if (gameData.away_team_id) {
          const { data: awayTeam } = await supabase
            .from('teams')
            .select('team_name')
            .eq('team_id', gameData.away_team_id)
            .maybeSingle();
          awayTeamName = awayTeam?.team_name || 'Away Team';
        }

        // Check if user is coach of either team
        const { data: coachData } = await supabase
          .from('team_coaches')
          .select('team_id')
          .eq('coach_id', user.id)
          .in('team_id', [gameData.home_team_id, gameData.away_team_id].filter(Boolean));

        // Check if user is owner of either team
        const { data: ownerData } = await supabase
          .from('teams')
          .select('team_id')
          .eq('created_by', user.id)
          .in('team_id', [gameData.home_team_id, gameData.away_team_id].filter(Boolean));

        isCoach = coachData && coachData.length > 0;
        isTeamOwner = ownerData && ownerData.length > 0;

        debugInfo = `Coach: ${isCoach}, Owner: ${isTeamOwner}, UserTeamId: ${userTeamId}, UserTeamSide: ${userTeamSide}`;
        
        if (coachData) debugInfo += `, CoachTeams: [${coachData.map(c => c.team_id).join(', ')}]`;
        if (ownerData) debugInfo += `, OwnerTeams: [${ownerData.map(o => o.team_id).join(', ')}]`;

        console.log('Team Association Result:', {
          isCoach,
          isTeamOwner,
          homeTeamName,
          awayTeamName,
          debugInfo
        });

        setTeamAssociation({
          isCoach,
          isTeamOwner,
          homeTeamName,
          awayTeamName,
          debugInfo
        });
      } catch (error) {
        console.error('Error checking team association:', error);
        setTeamAssociation(prev => ({
          ...prev,
          debugInfo: `Error: ${error}`
        }));
      }
      
      setLoading(false);
    };

    checkTeamAssociation();
  }, [user, gameData, userTeamId, userTeamSide]);

  const handleInputChange = (section: keyof CoachSectionData, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSave = () => {
    onSave(formData);
  };

  // Show loading state
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clipboard className="h-5 w-5" />
            Coach Management
          </CardTitle>
          <CardDescription>
            Checking your team association...
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>Loading coach information...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Main render
  return (
    <Card>
      <CardHeader className="px-4 py-5 md:px-6 md:py-6">
        <CardTitle className="text-lg md:text-xl flex items-center gap-2 flex-wrap">
          <Clipboard className="h-5 w-5" />
          Coach Management
          {!canEdit && <Badge variant="outline">View Only</Badge>}
        </CardTitle>
        <CardDescription className="text-sm md:text-base">
          Add players to roster, manage attendance responses, and confirm team readiness
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 px-4 py-5 md:px-6">
        {/* Debug Information (only in development) */}
        {process.env.NODE_ENV === 'development' && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>Debug Info</AlertTitle>
            <AlertDescription className="text-xs mt-2">
              {teamAssociation.debugInfo}
            </AlertDescription>
          </Alert>
        )}

        {/* Team Association Status */}
        {(!userTeamId || !userTeamSide) && !teamAssociation.isCoach && !teamAssociation.isTeamOwner ? (
          <div className="space-y-4">
            {/* Missing Team Association */}
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>No Team Association Found</AlertTitle>
              <AlertDescription>
                You are not associated with either team in this game as a coach or team owner.
                To manage player lineups and team readiness, you need to be:
                <ul className="list-disc list-inside mt-2 ml-4">
                  <li>Added as a coach to one of the teams</li>
                  <li>The owner of one of the teams</li>
                </ul>
                <div className="mt-3 text-sm">
                  <strong>Teams in this game:</strong>
                  <br />• Home: {teamAssociation.homeTeamName || 'Not set'}
                  <br />• Away: {teamAssociation.awayTeamName || 'Not set'}
                </div>
              </AlertDescription>
            </Alert>

            {/* Contact Information */}
            <Card className="bg-muted/50">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <Users className="h-5 w-5 mt-0.5 text-muted-foreground" />
                  <div>
                    <h4 className="text-sm font-semibold mb-2">Need Access?</h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      Contact your team owner or game organizer to be added as a coach for one of the teams.
                    </p>
                    <div className="text-xs text-muted-foreground">
                      Once you're added as a coach, you'll be able to:
                      <ul className="list-disc list-inside ml-2 mt-1">
                        <li>Manage the team roster</li>
                        <li>Set starting lineup and positions</li>
                        <li>Confirm team readiness</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (teamAssociation.isCoach || teamAssociation.isTeamOwner) && (
          <>
            {/* Team Association Warning for Team Owners without proper assignment */}
            {(!userTeamId || !userTeamSide) && (
              <Alert className="mb-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>This section requires your attention as a {teamAssociation.isCoach ? 'coach' : 'team owner'}</AlertTitle>
                <AlertDescription>
                  Complete this section to help move the game preparation forward.
                </AlertDescription>
              </Alert>
            )}

            {/* Determine team ID and side for team owners */}
            {(() => {
              let effectiveTeamId = userTeamId;
              let effectiveTeamSide = userTeamSide;
              
              // If team owner but no userTeamId set, determine from game data
              if (teamAssociation.isTeamOwner && !effectiveTeamId) {
                if (gameData.home_team_id && teamAssociation.debugInfo.includes(`[${gameData.home_team_id}]`)) {
                  effectiveTeamId = gameData.home_team_id;
                  effectiveTeamSide = 'home';
                } else if (gameData.away_team_id && teamAssociation.debugInfo.includes(`[${gameData.away_team_id}]`)) {
                  effectiveTeamId = gameData.away_team_id;
                  effectiveTeamSide = 'away';
                }
              }
              
              return effectiveTeamId && effectiveTeamSide ? (
                <>
                  {/* Workflow Instructions */}
                  <Alert className="p-4 md:p-6">
                    <Info className="h-5 w-5 md:h-4 md:w-4" />
                    <AlertTitle className="text-base md:text-lg">Coach Workflow</AlertTitle>
                    <AlertDescription className="text-sm md:text-base">
                      <ol className="list-decimal list-inside space-y-2 mt-2">
                        <li>Review and approve player join requests</li>
                        <li>Add players to your team roster</li>
                        <li>Players receive notifications to confirm attendance</li>
                        <li>Manage readiness status for attending players (1 hour before game)</li>
                        <li>Confirm team readiness</li>
                      </ol>
                    </AlertDescription>
                  </Alert>

                  {/* Join Request Approval */}
                  <JoinRequestApproval
                    gameId={gameData.id}
                    homeTeamId={gameData.home_team_id}
                    awayTeamId={gameData.away_team_id}
                    homeTeamName={teamAssociation.homeTeamName}
                    awayTeamName={teamAssociation.awayTeamName}
                    canEdit={canEdit}
                  />

                  {/* Player Roster Management */}
                  <TeamRosterManagement
                    gameId={gameData.id}
                    teamId={effectiveTeamId}
                    teamSide={effectiveTeamSide}
                    teamName={effectiveTeamSide === 'home' ? (teamAssociation.homeTeamName || 'Home Team') : (teamAssociation.awayTeamName || 'Away Team')}
                    canEdit={canEdit}
                    onPlayersChange={(players) => {
                      // Update form data when players change
                      const playerIds = players.map(p => p.id);
                      handleInputChange('players', 'selectedPlayers', playerIds);
                      // Trigger Starting Lineup refresh
                      setRosterRefreshKey(prev => prev + 1);
                    }}
                  />

                  {/* Starting Lineup Management */}
                  <StartingLineupManager
                    gameId={gameData.id}
                    teamSide={effectiveTeamSide}
                    teamName={effectiveTeamSide === 'home' ? (teamAssociation.homeTeamName || 'Home Team') : (teamAssociation.awayTeamName || 'Away Team')}
                    gameTableType={gameTableType}
                    onLineupChange={(starters) => {
                      console.log('Starting lineup changed in CoachSection:', starters);
                      setCurrentStartingLineup(starters);
                      handleInputChange('lineup', 'startingLineup', starters);
                    }}
                    canEdit={canEdit}
                    refreshKey={rosterRefreshKey}
                  />

                  {/* Game Day Readiness - unlocks 1hr before game */}
                  <GameDayReadiness
                    gameId={gameData.id}
                    teamSide={effectiveTeamSide}
                    teamName={effectiveTeamSide === 'home' ? (teamAssociation.homeTeamName || 'Home Team') : (teamAssociation.awayTeamName || 'Away Team')}
                    gameDate={gameData.game_date || (gameData as any).date}
                    gameTime={(gameData as any).start_time || gameData.game_time}
                    canEdit={canEdit}
                  />

                  {/* Player Readiness Management */}
                  <PlayerReadinessManager
                    gameId={gameData.id}
                    gameData={gameData}
                    userRole={teamAssociation.isCoach ? 'coach' : 'team_owner'}
                    teamId={effectiveTeamId}
                    teamSide={effectiveTeamSide}
                    teamName={effectiveTeamSide === 'home' ? (teamAssociation.homeTeamName || 'Home Team') : (teamAssociation.awayTeamName || 'Away Team')}
                  />

                  {/* Team Readiness */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold">Team Readiness</h4>
                    <TeamReadinessConfirmation
                      gameId={gameData.id}
                      teamId={effectiveTeamId}
                      teamSide={effectiveTeamSide}
                      selectedPlayers={formData.players.selectedPlayers}
                      startingLineup={currentStartingLineup}
                      isReady={(gameData as any)[`${effectiveTeamSide}_team_ready`] || false}
                      readinessDeadline={(gameData as any).readiness_deadline}
                      gameTableType={gameTableType}
                      canConfirmReadiness={canEdit}
                      onReadinessChange={(isReady) => {
                        console.log('Team readiness changed:', isReady);
                      }}
                    />
                  </div>

                  {/* Actions */}
                  {canEdit && (
                    <div className="flex justify-end">
                      <Button onClick={handleSave} disabled={saving}>
                        {saving ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            Save Coach Information
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertTitle>Team Association Detected</AlertTitle>
                  <AlertDescription>
                    You are associated with a team in this game but the system couldn't determine which side.
                    This might be a temporary issue. Try refreshing the page.
                    <div className="mt-2 text-sm">
                      Role: {teamAssociation.isCoach ? 'Coach' : 'Team Owner'}
                    </div>
                  </AlertDescription>
                </Alert>
              );
            })()}
          </>
        )}
      </CardContent>
    </Card>
  );
};